/**
 * [CurveBeamAI] App bootstrap – CurveBeam init, web-connect, early error pages, post-init customizations.
 * Imported by App.tsx so CurveBeam logic stays isolated here.
 */
import React from 'react';
import { CustomizationScope } from '@ohif/core';
import { environment } from '../services/config.service';
import { validateSession } from '../services/session-channel.service';
import type { SessionFailureKind } from '../services/session-channel.service';
import { SessionConnectionStateEnum } from '../models/SessionConnectionStateEnum';
import {
  AUTH_DATA_KEY,
  CONFIG_ERROR_PATH,
  getAccessTokenFromStorage,
  getInvalidSourcePath,
  getSessionExpiredPath,
  INVALID_SOURCE_PATH,
  isOnSessionPage,
  SESSION_EXPIRED_PATH,
  stripLaunchCodeFromUrl,
} from '../services/access-validation.service';
import { appInitErrorResolver } from '../utils/appInitErrorResolver';
import {
  isCurveBeamConfig,
  getCurveBeamAboutModalCustomization,
} from '../components/AboutModalCurveBeam';
import appInit from '@/appInit.js';

/**
 * [CurveBeamAI] How long to wait after `ls.authData` is removed in another tab before treating it as
 * a session end. Must exceed the library's clear→reconnect→re-fetch cycle on an abnormal (1006)
 * close, measured at ~1.4s, so a transient blip in one tab does not evict healthy tabs.
 */
const SESSION_END_GRACE_MS = 5000;

/** [CurveBeamAI] Render config-error / invalid-source pages before main app to avoid routing loops */
export function getCurveBeamEarlyPage(): React.ReactElement | null {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname;
  const lazyPage = path.includes(CONFIG_ERROR_PATH)
    ? React.lazy(() => import('../components/ConfigErrorPage'))
    : path.includes(SESSION_EXPIRED_PATH)
      ? React.lazy(() => import('../components/SessionExpiredPage'))
      : path.includes(INVALID_SOURCE_PATH)
        ? React.lazy(() => import('../components/InvalidSourcePage'))
        : null;
  return lazyPage ? (
    <React.Suspense fallback={<div>Loading...</div>}>
      {React.createElement(lazyPage)}
    </React.Suspense>
  ) : null;
}

/**
 * [CurveBeamAI] Hook: init flow. With session_id + web-connect: wait for validateSession → appInit.
 * Without: appInit immediately.
 *
 * Returns { init, statusPage, shouldRedirectToSessionExpired, shouldRedirectToInvalidSource } - at
 * most one of the two redirects is ever true, and neither is when the failure is not a session one.
 */
export function useCurveBeamAppInit(
  config: AppTypes.Config,
  defaultExtensions: unknown[],
  defaultModes: unknown[]
) {
  const [init, setInit] = React.useState<Awaited<ReturnType<typeof appInit>> | null>(null);
  const [sessionState, setSessionState] = React.useState(SessionConnectionStateEnum.PENDING);
  const [initError, setInitError] = React.useState('');
  // [WTD-1826] Null until a session failure is diagnosed, and it stays null for failures that are
  // not about the session - see SessionFailureKind.
  const [sessionFailure, setSessionFailure] = React.useState<SessionFailureKind | null>(null);

  const urlParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const hasSessionId = !!urlParams?.get('session_id');
  // [WTD-1826] The appSync object replaces webSocketURL + clientId, and its PRESENCE selects the
  // flow. Deliberately the container and not the endpoints inside it, which is the opposite of what
  // validateSession asks a few lines later - the two questions are different on purpose:
  //
  //   absent entirely  -> an environment that has not been migrated. Falling back is correct.
  //   present, partial -> somebody wrote a broken profile. Falling back would boot the viewer with
  //                       no session validation at all and no indication anything was wrong.
  //
  // So a half-configured pair must still select this flow, so that validateSession can reject it
  // with a message naming the configuration key. Testing the endpoints HERE would turn a broken
  // profile into a silent downgrade, which is strictly harder to diagnose than an error page.
  const hasSessionChannelConfig = !!environment?.appSync;
  const useSessionChannelFlow = hasSessionId && hasSessionChannelConfig;

  React.useEffect(() => {
    document.title = environment?.region ? `Viewer - ${environment.region}` : 'Viewer';
  }, [environment?.region]);

  // [WTD-1826] The one state that fails silently, so it is the one that has to announce itself.
  //
  // A launch URL carries session_id because DMP put it there, while appSync comes from THIS
  // application's AppConfig profile - two profiles, deployed separately. Update DMP first and the
  // viewer has a session it cannot join: the switch above is false, so the effect below runs
  // appInit immediately and the viewer starts with no session channel, no launch-code redemption
  // and no cross-tab session-end detection. Nothing is thrown and no error page appears; it simply
  // behaves like a viewer that was never part of a session, which is indistinguishable from working
  // until somebody notices a signed-out user still reading images.
  //
  // (validateAccess still runs regardless - referrer, session_id, tampering - so this is a
  // downgrade rather than an unguarded viewer.)
  //
  // The launch code is dropped here rather than left in the address bar. Normally
  // validateSession removes it after redeeming it, but validateSession is exactly what does not
  // run in this state, so without this the credential stays in the URL and in history for the
  // life of the page. It is worthless after about thirty seconds and nothing here can redeem it,
  // so there is no reason to keep it.
  React.useEffect(() => {
    if (!hasSessionId || hasSessionChannelConfig) return;
    console.warn(
      '[CurveBeam] session_id is present but appSync is not configured. The viewer will start ' +
        'with no session channel: no launch-code redemption, no renewToken handling and no ' +
        'cross-tab session-end detection. Check the appSync endpoints in this environment AppConfig profile.'
    );
    stripLaunchCodeFromUrl();
  }, [hasSessionId, hasSessionChannelConfig]);

  const runAppInit = React.useCallback(() => {
    appInit(config, defaultExtensions, defaultModes)
      .then(setInit)
      .catch(err => {
        console.error(err);
        setInitError(err?.message || 'App initialization failed');
      });
  }, [config, defaultExtensions, defaultModes]);

  React.useEffect(() => {
    if (!useSessionChannelFlow) {
      runAppInit();
      return;
    }
    if (sessionState === SessionConnectionStateEnum.DISCONNECTED) return;
    if (sessionState === SessionConnectionStateEnum.PENDING) {
      // Caught, because the returned promise was previously dropped. validateSession is async, so a
      // SYNCHRONOUS throw inside it - SessionChannelService.connect calls setInitialToken before it
      // returns a promise at all, and `new ConnectionOptions` runs just above - surfaces as a
      // rejection here rather than as an exception. Unhandled, it leaves sessionState on PENDING and
      // the user on 'Pending DMP Access Token validation...' for as long as the tab is open.
      //
      // Distinct from the rejection handler on connect() inside validateSession: that one cannot see
      // this, because the connect promise is not awaited or returned there, and this one cannot see
      // that, for the same reason. Both are needed.
      validateSession({ setSessionState, setInitError, setSessionFailure }).catch(
        (error: unknown) => {
          console.error('[SessionChannel] validateSession threw', error);
          setInitError(
            'There has been an error establishing your session. Please contact CurveBeam AI support team.'
          );
          setSessionFailure('config');
          setSessionState(SessionConnectionStateEnum.DISCONNECTED);
        }
      );
      return;
    }
    if (sessionState === SessionConnectionStateEnum.CONNECTED) {
      runAppInit();
    }
  }, [useSessionChannelFlow, sessionState, runAppInit]);

  // [CurveBeamAI] Cross-tab session-end detection.
  //
  // When the session ends (e.g. DMP's idle timeout), web-connect's handleOnClose calls clearTokens(),
  // which removes the shared `ls.authData`. It only notifies the app via connectionClosedSubject for
  // close reasons 'app-logout' / 'app-error'; every other close returns silently. Tabs that hit that
  // silent path therefore lose their token but are never told, and keep displaying images for an
  // ended session — observed with 8 tabs open, where only the newest reached the 401 screen.
  //
  // `localStorage` removal raises a `storage` event in every OTHER same-origin tab, so keying off the
  // removal itself is strictly more reliable than waiting for a notification the library may not send.
  // (The tab that did the clearing gets no event, and does not need one — it already has its own
  // socket-close handling.)
  //
  // Two conditions must NOT trigger a redirect, or this would be worse than the current behaviour:
  //   1. Writes. Token renewal (`renewToken` → setAuthData) writes this same key; only a removal
  //      (newValue === null) signals a session end.
  //   2. Transient clears. An abnormal close (code 1006) also calls clearTokens(), but the library
  //      then reconnects and re-fetches — measured at ~1.4s end to end. Redirecting immediately would
  //      bounce every other tab to invalid-source over one tab's momentary network blip. So we wait
  //      out a grace window and only redirect if the token has genuinely not come back.
  React.useEffect(() => {
    if (!useSessionChannelFlow || typeof window === 'undefined') return;
    // Already on an error page - nothing to redirect, and avoids a navigation loop: landing on
    // invalid-source and then being bounced to session-expired by a storage event is the same loop
    // wearing a different name.
    //
    // Belt and braces rather than load-bearing. App returns getCurveBeamEarlyPage() before calling
    // this hook, so on any error page the hook never runs and this effect never registers. Asked
    // through the shared predicate anyway, so a fifth page does not need finding here.
    if (isOnSessionPage()) return;

    let pendingCheck: number | undefined;

    const onStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_DATA_KEY) return;

      if (event.newValue !== null) {
        // Token was (re)written — a renewal, or a reconnect that recovered. Stand down.
        if (pendingCheck !== undefined) {
          clearTimeout(pendingCheck);
          pendingCheck = undefined;
        }
        return;
      }

      if (pendingCheck !== undefined) return; // already waiting out the grace window
      pendingCheck = window.setTimeout(() => {
        pendingCheck = undefined;
        // Re-read rather than trusting the event: a reconnect may have restored the token meanwhile.
        if (!getAccessTokenFromStorage()) {
          // The token was present and has gone: this session ended, it did not fail to start.
          redirectToSessionExpired();
        }
      }, SESSION_END_GRACE_MS);
    };

    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      if (pendingCheck !== undefined) clearTimeout(pendingCheck);
    };
  }, [useSessionChannelFlow]);

  // Loading/error UI. sessionFailure is passed so a configuration fault can be given a heading -
  // see appInitErrorResolver - not so it can change which redirect fires.
  const statusPage = appInitErrorResolver(
    init,
    sessionState,
    initError,
    useSessionChannelFlow,
    sessionFailure
  );

  // [WTD-1826] Driven by WHICH failure occurred rather than by "an error was set at all", which is
  // what the first version did. Any initError redirected, and three of its sources do not mean a
  // session ended: the no-credential path, whose own message is literally 'Please open this viewer
  // from DMP.'; the missing-configuration path; and appInit throwing, where the session is healthy
  // and the application is not. The last was the worst of the three, because the redirect runs
  // before statusPage renders, so the real message - which extension failed to load - was discarded
  // and replaced by a session expiry that had not happened.
  //
  // The case that will be met in practice is the first: a launch code lives thirty seconds, so a
  // slow first load, a double-clicked DMP link or a bookmarked launch URL all arrive with nothing
  // to redeem. That is a viewer that was never given a session, and invalid-source is the page that
  // says so.
  //
  // Both stay false when nothing set a kind, which leaves statusPage to render the message.
  const shouldRedirectToSessionExpired = sessionFailure === 'ended';
  const shouldRedirectToInvalidSource = sessionFailure === 'no-session';

  return { init, statusPage, shouldRedirectToSessionExpired, shouldRedirectToInvalidSource };
}

/** [CurveBeamAI] Redirect to invalid-source - the viewer was opened without a usable session */
export function redirectToInvalidSource(): void {
  window.location.replace(getInvalidSourcePath());
}

/** [WTD-1826] Redirect to session-expired - the session was live and has ended */
export function redirectToSessionExpired(): void {
  window.location.replace(getSessionExpiredPath());
}

/**
 * [CurveBeamAI] Post-init: inject Bearer token for API calls, apply CurveBeam customizations.
 */
export function applyCurveBeamPostInit(init: Awaited<ReturnType<typeof appInit>>): void {
  const { servicesManager } = init;
  const { userAuthenticationService, customizationService } = servicesManager.services;
  const { extensionManager } = init;

  // Always use localStorage for scan/API auth – web-connect stores token in ls.authData.
  // Config API does not return accessToken; read on each call so renewed token is used.
  // AWS Health Imaging (and API Gateway) requires "Bearer <token>" format; raw token without
  // scheme causes "Invalid key=value pair (missing equal-sign)" errors.
  userAuthenticationService.setServiceImplementation({
    getAuthorizationHeader: () => {
      let token = getAccessTokenFromStorage();
      if (!token) return {};
      // Normalize: if token already has Bearer prefix (e.g. from some storage), use as-is; else add it
      const authValue = token.trimStart().toLowerCase().startsWith('bearer ')
        ? token.trimStart()
        : `Bearer ${token}`;
      return { Authorization: authValue };
    },
  } as any);

  customizationService.init(extensionManager);
  if (isCurveBeamConfig()) {
    customizationService.setCustomizations(
      {
        ...getCurveBeamAboutModalCustomization(), // CurveBeam branding in About modal
        'studyBrowser.hideFilterBar': true,
      },
      CustomizationScope.Global
    );

    if ((init as any).appConfig) {
      (init as any).appConfig.investigationalUseDialog = { option: 'never' };
    }
  }
}
